<template>
  <picture
    v-observe-visibility="{
      callback: handleScrollIntoView,
      once: true
    }"
    :class="bemBlock"
  >
    <img
      ref="img"
      :class="[
        bemElement('img', {
          placeholder: placeholder && placeholder.url && !loaded,
          loading: !loaded,
          loaded: loaded
        }),
        imageClasses
      ]"
      :sizes="sizes"
      :src="shouldLoad ? src : placeholderSrc"
      :srcset="shouldLoad && !error ? srcset : ''"
      :data-placeholder="placeholderSrc"
      :data-src="shouldLoad ? '' : src"
      :data-srcset="shouldLoad ? '' : srcset"
      :alt="image.alt"
      :loading="loading"
      :title="useTitle ? image.title : null"
      itemprop="image"
      draggable="false"
      @error.prevent="handleImageError"
      @load.prevent="handleImageLoad"
      @click="$emit('image-click', $event)"
    >
  </picture>
</template>

<script>
import bemClassesMixin from '../mixins/bemClassesMixin';
import checkVisibilityMixin from '../mixins/checkVisibilityMixin';

export default {
  name: 'BaseImage',

  mixins: [bemClassesMixin, checkVisibilityMixin],

  props: {
    /** @type {ProductImage} */
    image: {
      type: Object,
      required: true,
    },
    imageClasses: {
      type: [String, Array],
      required: false,
      default: () => [],
    },
    /**
     * Same spec as HTML specification:
     * https://html.spec.whatwg.org/multipage/urls-and-fetching.html#lazy-loading-attributes
     *
     * However, default value will be 'lazy'
     */
    loading: {
      type: String,
      required: false,
      default: 'lazy',
      validator: (prop) => ['eager', 'lazy'].includes(prop),
    },
    /** @type {ProductImage} */
    placeholder: {
      type: Object,
      required: false,
      default: () => ({
        url: null,
      }),
    },
    sizes: {
      type: String,
      required: false,
      default: null,
    },
    srcset: {
      type: String,
      required: false,
      default: null,
    },
    useTitle: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data: () => ({
    /** @type {boolean} */
    error: false,
    /** @type {boolean} True if lazy image has finally been loaded */
    loaded: false,
    /** @type {boolean} True if lazy image has to be loaded because of becoming
     * visible on screen */
    shouldLoad: false,
  }),

  computed: {
    /** @type {string} Url to placeholder image. If the prop is empty, then the
     * data URI of the SVG is used */
    placeholderSrc() {
      return this.placeholder?.url ?? this.PLACEHOLDER_DATA_URI;
    },
    src() {
      if (this.error) return this.PLACEHOLDER_DATA_URI;
      return this.image?.url ?? this.PLACEHOLDER_DATA_URI;
    },
  },

  watch: {
    image() {
      this.reset();
    },
    loading() {
      if (!this.loaded) {
        // Force recalculate shouldLoad and loading
        // But only when the image has not been loaded yet
        // Ex:
        //  - from "eager" to "lazy"
        //  - from visible "lazy" (and loaded) to "eager"
        this.reset();
      }
    },
  },

  created() {
    this.PLACEHOLDER_DATA_URI = this.generatePlaceholderDataUri(2, 3);
    this.reset();
  },

  methods: {
    /**
     * Generates a data URI of a custom placeholder rectangle
     * @param {number} w - Width
     * @param {number} h - Height
     * @param {string} [color='#fafafa'] - Background color of the placeholder image.
     * Default: $color__alabaster
     */
    generatePlaceholderDataUri(w = 2, h = 3, color = '#fafafa') {
      // const max = w > h ? w : h;
      const placeholderSvg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${w} ${h}'><rect width='${w}' height='${h}' fill='${color}'/></svg>`;
      return `data:image/svg+xml,${encodeURIComponent(placeholderSvg)}`;
    },
    handleImageError() {
      this.error = true;
    },
    handleImageLoad(event) {
      if (this.shouldLoad) {
        this.loaded = true;
        this.$emit('load', event);
      }
    },
    handleScrollIntoView(isVisible) {
      this.handleVisibilityChanged(isVisible);
      if (isVisible) {
        this.shouldLoad = true;
      }
    },
    reset() {
      this.loaded = false;
      this.shouldLoad = this.loading === 'eager';
      this.checkVisibility(this.handleScrollIntoView);
      if (this.$refs.img?.complete) {
        this.handleImageLoad();
      }
    },
  },
};
</script>
